import axios from 'axios';
import Map from '../Map';
import PageTitle from '../PageTitle';
import EventBus from '../../utilities/EventBus';
import { isTouchDevice, getLocation } from '../../utilities';
import { TweenLite, ScrollToPlugin, Power2 } from 'gsap/all';
import NProgress from 'nprogress';
import Share from '../Share';
import ScrollDepth from '../../utilities/ScrollDepth';

const plugins = [ScrollToPlugin];

class App {
  constructor() {
    this.eventBus = new EventBus();
    this.ui = {
      body: document.body,
      map: document.querySelector('.js-map'),
      pageContent: document.querySelector('.js-page'),
      win: window
    };
    this.sizes = {
      winWidth: this.ui.win.innerWidth,
      winHeight: this.ui.win.innerHeight
    };
    this.settings = {
      url: window.SITE_URL,
      easing: 'easeInOutCubic'
    };
    this.state = {};
    this.scrollDepth = new ScrollDepth();
    this.scrollDepth.setPath(location.pathname);

    // initialise components
    this.map = new Map(this.ui.map, this.eventBus);
    this.landing = document.querySelector('.js-landing');
    this.pageTitle = new PageTitle(document.querySelector('.js-page-title'), this.eventBus);
    this.headerLogo = document.querySelector('.js-header-logo');
    this.menuButton = document.querySelector('.js-menu-button');
    this.overlayClick = document.querySelector('.js-overlay-click-layer');
    this.startExploring = document.querySelector('.js-start-exploring');
    this.menuItems = document.querySelectorAll('.menu__item');

    // bind functions to this
    this.handleHistory = this.handleHistory.bind(this);
    this.handleBodyClick = this.handleBodyClick.bind(this);
    this.handleMenuButtonClick = this.handleMenuButtonClick.bind(this);
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
    this.resize = this.resize.bind(this);
    this.closeContent = this.closeContent.bind(this);
    this.checkSingle = this.checkSingle.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleMenuItems = this.handleMenuItems.bind(this);

    NProgress.configure({
      showSpinner: false
    });

    this.init();
  }

  init() {
    if (!isTouchDevice()) this.ui.body.classList.add('no-touch');

    this.checkInternalLinks();
    this.checkOutboundLinks();
    this.bindEventListeners();
    this.setInitialState();
    this.checkShare();

    this.scrollDepth.init();

    // check if homepage.
    if (this.checkIfHomepage()) {
      // display welcome message
      this.ui.body.classList.add('is-landing-show');

      // init load map and display markers
      this.map.init()
        .then(res => {
          this.map.displayMarkers()
            .then(() => {
              this.map.displayMap();
            });
        });
    } else {
      this.map.init()
        .then(() => this.map.displayMarkers())
        .then(() => {
          this.map.displayMap();

          if (history.state.locationId) {
            this.map.selectLocation(parseInt(history.state.locationId))
              .then(() => {
                this.checkSingle();
              });
          } else {
            this.checkSingle();
          }
        });
    }
  }

  checkIfHomepage() {    
    return !this.ui.body.classList.contains('single');
  }

  bindEventListeners() {
    window.addEventListener('popstate', this.handleHistory);

    this.menuButton.addEventListener('click', this.handleMenuButtonClick, false);
    this.ui.map.addEventListener('click', this.handleBodyClick, false);
    this.headerLogo.addEventListener('click', () => {
      this.closeContent();

      // track header clicking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Logo Click',
        'event_category': 'Header',
        'event_label': 'Navigation'
      });
    });

    this.overlayClick.addEventListener('click', this.handleOverlayClick);

    // Eventlistener for internal links
    document.addEventListener('click', (e) => {
      // TODO: might need a polyfill for 'closest'.
      if(!e.target.closest('.internal-link')) return;
      e.preventDefault();
      
      const linkEl = e.target.closest('.internal-link');
      
      const url = linkEl.getAttribute('href');
      const loc = getLocation(url);
      const locationId = linkEl.getAttribute('data-location-id') || '';
      
      if (location.pathname !== loc.pathname) {
        this.handleMenuToggle(true);
        this.update(url, locationId);
      }
    }, false);

    this.startExploring.addEventListener('click', () => {
      TweenLite.to(this.landing, 0.4, {
        opacity: 0,
        onComplete: () => {
          this.ui.body.classList.remove('is-landing-show');
          // this.map.displayMap();
        }
      });
    });

    // add listeners to menu__item to track engagement
    for(let i = 0; i < this.menuItems.length; i++) {
      this.menuItems[i].addEventListener('click', this.handleMenuItems);
    }

    this.ui.win.addEventListener('resize', this.resize);
  }

  setInitialState() {
    this.state.nav = this.ui.body.classList.contains('single') ? 'single' : 'index';
    this.state.locationId = this.ui.body.dataset.locationId || '';

    if (history.replaceState) {
      history.replaceState({
        state: this.state.nav,
        url: window.location.href,
        locationId: this.state.locationId
      }, null, null);

      // console.log('inital history', history);
    }
  }

  checkSingle() {
    if (this.ui.body.classList.contains('single')) {
      this.scrollContent();
    }
  }

  resize() {
    this.sizes.winWidth = this.ui.win.innerWidth;
    this.sizes.winHeight = this.ui.win.innerHeight;
  }

  handleBodyClick(e) {
    e.preventDefault();
    this.handleMenuToggle(true);
  }

  handleMenuButtonClick(e) {
    e.preventDefault();
    if (!this.ui.body.classList.contains('is-show-menu')) {
      // open menu
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Open',
        'event_category': 'Menu',
        'event_label': 'Navigation'
      });
    } else {
      // close menu
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Close',
        'event_category': 'Menu',
        'event_label': 'Navigation'
      });
    }

    this.handleMenuToggle();
  }

  handleMenuToggle(close) {
    if (close) {
      this.ui.body.classList.remove('is-show-menu');
    } else {
      this.ui.body.classList.toggle('is-show-menu');
    }
  }

  handleMenuItems(e) {
    let menuItemLink = e.currentTarget.querySelector('a');

    if (menuItemLink) {
      // track menu link click
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Select Location',
        'event_category': 'Menu',
        'event_label': 'Navigation',
        'value': menuItemLink.href
      });
    }
  }

  handleOverlayClick() {
    this.closeContent();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Return to map',
      'event_category': 'Map',
      'event_label': 'Navigation'
    });
  }

  checkInternalLinks() {
    const internalLinks = document.querySelectorAll(`a[href*="${window.SITE_URL}"]`);
    [...internalLinks].forEach((link, index) => {
      link.classList.add('internal-link');
    });
  }

  checkOutboundLinks() {
    const outBoundLinks = document.querySelectorAll(`a[href]:not([href*="${window.SITE_URL}"])`);
    window.dataLayer = window.dataLayer || [];

    [...outBoundLinks].forEach(obLink => {
      obLink.classList.add('outbound-link');

      obLink.addEventListener('click', () => {
        // track outbound links
        ga('gtm1.send', 'event', 'outbound', 'click', obLink.href, {});
      });
    });
  }

  checkShare() {
    /* share buttons */
    const shareButtons = document.querySelectorAll('[data-share]');

    if(shareButtons.length) {
      for(let i = 0; i < shareButtons.length; i++) {
        new Share(shareButtons[i]);
      }
    }
  }

  update(url, locationId = '', fromBack = false) {
    if (locationId) {
      if (locationId !== this.state.locationId) {
        // location is different: close content, update map, update content
        this.hideContent(() => {
          this.map.selectLocation(locationId)
            .then(() => this.updateContent(url, locationId, fromBack))
        });
      } else {
        this.map.selectLocation(locationId)
        .then(() => this.updateContent(url, locationId, fromBack));
      }
    } else {
      // no location: reset map
      this.map.mapClicked()
        .then(() => this.updateContent(url, locationId, fromBack));
    }
  }

  updateContent(url, locationId = '', fromBack = false) {
    this.ui.body.classList.add('loading');
    this.state.locationId = locationId;

    TweenLite.to(window, 0.25, {
      scrollTo: {
        y: 0,
        autoKill:false,
      },
      ease: this.settings.easing,
      onComplete: () => {
        this.loadContent(url, locationId, fromBack);
      }
    });
  }

  loadContent(url, locationId, fromBack = false) {
    this.ui.body.classList.add('loading');
    NProgress.start();

    axios.get(url)
      .then(res => {
        // wrap content so it's accessible
        const wrapper = document.createElement('div');
        wrapper.innerHTML = res.data;
        const pageContent = wrapper.querySelector('.js-page');

        // update state
        this.ui.body.classList.add('single');
        this.state.nav = 'single';

        if (!fromBack) {
          this.changeState(url, this.state.nav, locationId);
        }

        this.ui.pageContent.classList.remove('is-hidden');
        this.ui.pageContent.innerHTML = pageContent.innerHTML;

        this.scrollContent();

        this.ui.body.classList.remove('loading');
        this.checkInternalLinks();
        this.checkOutboundLinks();
        this.checkShare();
        NProgress.done();
      })
      .catch(err => {
        console.log('err', err);
      });
  }

  hideContent(cb) {
    TweenLite.to(window, 0.25, {
      scrollTo: {
        y: 0,
        autoKill:false,
      },
      ease: this.settings.easing,
      onComplete: () => {
        this.ui.body.classList.remove('single');
        this.ui.pageContent.classList.add('is-hidden');
        this.ui.pageContent.innerHTML = '';
        cb();
      }
    });
  }

  closeContent(fromBack = false) {
    this.state.nav = 'index';
    this.state.locationId = '';

    TweenLite.to(document.body, 0.25, {
      scrollTo: {
        y: 0,
        autoKill: false
      },
      ease: this.settings.easing,
      onComplete: () => {
        this.ui.body.classList.remove('single');
        this.ui.pageContent.classList.add('is-hidden');
        this.ui.pageContent.innerHTML = '';
      }
    });

    if (!fromBack) {
      this.changeState(this.settings.url, 'index', null);
    }
  
    this.map.mapClicked();
  }

  scrollContent() {
    TweenLite.to(window, 0.45, {
      scrollTo: {
        y: this.sizes.winHeight - 80,
        autoKill:false
      },
      ease: this.settings.easing
    });
  }

  changeState(url, state, locationId = '') {
    if (history.pushState) {
      history.pushState({
        state,
        url,
        locationId
      }, null, url);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Pageview',
        'url': location.pathname
      });

      // reset ScrollDepth if new page.
      this.scrollDepth.reset();
      this.scrollDepth.setPath(location.pathname);

      // console.log('changing state to ' + state);
      // console.log('changing history to', history.state);
    }
  }

  handleHistory(e) {
    const state = e.state;
    if (state) {
      switch(state.state) {
        case 'single':
          // console.log('single');
          // display content, load from url, update state an url in close content
          this.update(state.url, state.locationId, true);
          break;
        case 'index':
          // console.log('index');
          // close content, update state an url in close content
          this.closeContent(true);
          break;
        default:
          // console.log(state.state);
      }
    }
  }
}

export default App;